import { QuestionMarkIcon } from 'assets/icons';
import PanelCard from 'components/card/PanelCard';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import { formatEuros } from 'components/invoiceList/invoiceAmount';
import { formatParisDay } from 'components/invoiceList/invoiceStatusTooltipText';
import UseContext from 'hooks/useContext';
import InvoiceAppendices from './InvoiceAppendices';
import InvoiceCompanyTerms from './InvoiceCompanyTerms';
import {
  categoryText,
  companyLabelOf,
  formatPeriod,
  invoiceErrorTexts,
  paymentTermText,
} from './invoiceDetailText';

const DASH = '—';

const isAmount = (value) => typeof value === 'number' && Number.isFinite(value);
const amountOrDash = (value) => (isAmount(value) ? formatEuros(value) : DASH);

const Field = ({ label, value, wide = false }) => (
  <div
    className={`px-4 py-3 border-b border-gray-100 last:border-b-0 ${
      wide ? 'col-span-2' : ''
    }`}
  >
    <dt className="text-sm text-gray-900">{label}</dt>
    <dd className="text-sm text-gray-500">{value}</dd>
  </div>
);

const NotImportedBanner = ({ invoice, text }) => (
  <div role="note" className="flex gap-3 rounded-lg bg-blue-50 px-4 py-3">
    <QuestionMarkIcon wh={20} color="#1E40AF" />
    <div className="flex flex-col gap-1 min-w-0">
      <div className="font-medium text-blue-900">
        {text('banner.notImportedTitle', 'Facture pas encore importée')}
      </div>
      <div className="text-sm text-blue-800">
        {invoice.importStatus === 'fetched'
          ? text(
              'banner.notImportedFetchedText',
              'Elle est enregistrée dans Jobbiz et sera importée dans Pennylane au prochain import.',
            )
          : text(
              'banner.notImportedText',
              'Elle sera enregistrée dans Jobbiz et importée dans Pennylane au prochain import.',
            )}
      </div>
    </div>
  </div>
);

const ErrorBanner = ({ invoice, text, errorText, journalText }) => {
  const { label, explanation, fix } = invoiceErrorTexts(
    errorText,
    journalText,
    invoice,
  );
  return (
    <>
      <div role="alert" className="rounded-lg bg-red-50 px-4 py-3">
        <div className="font-medium text-red-800">
          {fillText(text('banner.errorTitle', '{label} · {code}'), {
            label,
            code: invoice.error.code,
          })}
        </div>
        <div className="text-sm text-red-700">{explanation}</div>
      </div>
      <div className="flex gap-3 rounded-lg border border-gray-200 px-4 py-3">
        <QuestionMarkIcon wh={20} color="#111827" />
        <div className="flex flex-col gap-1 min-w-0">
          <div className="font-medium text-gray-900">
            {journalText('howToFix', 'Comment corriger')}
          </div>
          <div className="text-sm text-gray-600">{fix}</div>
        </div>
      </div>
    </>
  );
};

const LateBanner = ({ invoice, text }) => {
  const days = invoice.lateDays;
  const remaining = invoice.payment?.remainingAmount;
  const date = formatParisDay(invoice.dueDate) ?? DASH;
  return (
    <div role="note" className="rounded-lg bg-yellow-50 px-4 py-3">
      <div className="font-medium text-yellow-800">
        {days === 1
          ? text('banner.lateTitleOne', 'Échéance dépassée de 1 jour')
          : fillText(
              text('banner.lateTitleMany', 'Échéance dépassée de {days} jours'),
              { days },
            )}
      </div>
      <div className="text-sm text-yellow-700">
        {isAmount(remaining)
          ? fillText(
              text(
                'banner.lateText',
                'Reste à payer : {remaining} · échéance au {date}.',
              ),
              { remaining: formatEuros(remaining), date },
            )
          : fillText(
              text('banner.lateTextNoRemaining', 'Échéance au {date}.'),
              { date },
            )}
      </div>
    </div>
  );
};

/**
 * The « Informations » tab of the invoice detail (JBWEB-1274): a banner for
 * the status, the invoice's information and, once imported, its payment.
 * `categories` is the Pennylane categories list, null while read. `onEdit`
 * opens the modal of the category and the term (JBWEB-1275, JBWEB-1276):
 * one pencil, on the card, and none for an invoice without a company, which
 * the api refuses to edit. The company's post-it, remuneration conditions and
 * bonuses follow, from `company` (`useInvoiceCompany`), then its appendices
 * (JBWEB-1277), for an invoice with a company only: the api takes none
 * without one (409). `onAppendixConfirmed` runs when an admin confirms an
 * appendix.
 */
const InvoiceDetailInformation = ({
  invoice,
  categories,
  company,
  text,
  onEdit,
  onAppendixConfirmed,
}) => {
  const { language } = UseContext();
  const errorText = (key, fallback) =>
    language.getValue(`invoiceErrors.${key}`, fallback);
  const journalText = (key, fallback) =>
    language.getValue(`invoiceJournal.${key}`, fallback);
  const info = (key, fallback) => text(`information.${key}`, fallback);
  const payment = (key, fallback) => text(`payment.${key}`, fallback);
  const isImported = invoice.importStatus === 'imported';

  return (
    <div className="flex flex-col gap-4">
      {invoice.status === 'not_imported' && (
        <NotImportedBanner invoice={invoice} text={text} />
      )}
      {invoice.status === 'error' && invoice.error && (
        <ErrorBanner
          invoice={invoice}
          text={text}
          errorText={errorText}
          journalText={journalText}
        />
      )}
      {invoice.lateDays > 0 && <LateBanner invoice={invoice} text={text} />}

      <PanelCard
        title={info('title', 'Informations')}
        onEdit={invoice.company && onEdit ? onEdit : null}
      >
        <dl className="grid grid-cols-2">
          <Field
            label={info('amount', 'Montant TTC')}
            value={amountOrDash(invoice.amountInclTax)}
          />
          <Field
            label={info('period', 'Période')}
            value={formatPeriod(invoice.period)}
          />
          <Field
            label={info('company', 'Entreprise')}
            value={companyLabelOf(text, invoice)}
          />
          <Field
            label={info('category', 'Catégorie')}
            value={categoryText(text, invoice, categories)}
          />
          <Field
            wide
            label={info('paymentTerm', 'Délai de paiement')}
            value={paymentTermText(text, invoice, language)}
          />
        </dl>
      </PanelCard>

      {invoice.company && (
        <InvoiceCompanyTerms
          company={company.company}
          failed={company.failed}
          companyName={companyLabelOf(text, invoice)}
          text={text}
        />
      )}

      {isImported && (
        <PanelCard title={payment('title', 'Paiement')}>
          <dl className="grid grid-cols-2">
            <Field
              label={payment('remaining', 'Reste à payer')}
              value={amountOrDash(invoice.payment?.remainingAmount)}
            />
            <Field
              label={payment('paid', 'Montant réglé')}
              value={amountOrDash(invoice.payment?.paidAmount)}
            />
            <Field
              wide
              label={payment('paidAt', 'Payée le')}
              value={formatParisDay(invoice.payment?.paidAt) ?? DASH}
            />
          </dl>
        </PanelCard>
      )}

      {invoice.company && (
        <InvoiceAppendices
          invoice={invoice}
          onConfirmed={onAppendixConfirmed}
        />
      )}
    </div>
  );
};

export default InvoiceDetailInformation;
